import { get } from '@/services/http'
import { endpoints } from '@/services/endpoints'
import { sameLocation } from '@/features/shiftHandovers/api'

/**
 * What a record filed at one place may link to — LIVE.
 *
 * A shift handover links work orders, log book entries and anomalies; a log
 * book entry links one anomaly and one work order. Both writes run
 * `verify_linked_rows`, which accepts a link only at EXACTLY the record's own
 * place (`_same_location`): a line-level entry cannot cite a work order raised
 * on one of that line's machines. `LinkPickerDialog` is the one picker for all
 * of them, and this is what it reads.
 *
 * ── Rows beneath the place come back too ─────────────────────────────────
 * Each list is read from the resource that owns it, filtered by the record's
 * place, searched and paged by the server. That filter matches the place's
 * whole subtree (`apply_asset_ids` joins through the hierarchy), and none of
 * the three lists can ask for an exact match (BACKEND-GAPS.md). So every row is
 * marked `exact` or not, and the picker lists the rest disabled — dropping them
 * would leave short pages under a total that counts them.
 */

/** Comfortably under the lists' ceiling of 200, and what the place dialog pages at. */
export const LINK_PAGE_SIZE = 50

export const LINK_SOURCES = {
  workOrders: { path: endpoints.workOrders.list, key: 'work_orders' },
  logbookEntries: { path: endpoints.logbook.list, key: 'logbook_entries' },
  anomalies: { path: endpoints.anomalies.list, key: 'anomalies' },
}

/**
 * One page.
 *
 * @param {'workOrders'|'logbookEntries'|'anomalies'} kind
 * @param {{plantId: string|null, lineId: string|null, machineId: string|null}} place
 * @param {{search?: string, page?: number, pageSize?: number}} params
 */
export async function fetchLinkPage(kind, place, { search, page = 1, pageSize = LINK_PAGE_SIZE } = {}, signal) {
  const source = LINK_SOURCES[kind]
  const query = search?.trim()
  const data = await get(source.path, {
    signal,
    params: {
      ...(place.machineId
        ? { machine_id: place.machineId }
        : place.lineId
          ? { line_id: place.lineId }
          : { plant_id: place.plantId }),
      ...(query ? { search: query } : {}),
      sort_by: 'created_at',
      sort_dir: 'desc',
      page,
      page_size: pageSize,
    },
  })

  const items = (data?.[source.key] ?? []).map((row) => {
    const at = {
      plantId: row.plant_id ?? null,
      lineId: row.line_id ?? null,
      machineId: row.machine_id ?? null,
    }
    return {
      id: row.id,
      serialNumber: row.serial_number ?? row.id,
      title: row.title ?? '',
      exact: sameLocation(at, place),
      /* The deepest name is the row's own place — what a disabled row says. */
      where: row.hierarchy?.machine ?? row.hierarchy?.line ?? row.hierarchy?.plant ?? null,
    }
  })

  return { items, total: data?.total ?? items.length }
}
